/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/**
 * Exposes reusable status functions that apply the central KPI status rules
 * to the raw values required by overview and detail configurations.
 */

import {
    BASE_LOAD_COST_DAY_STATUS_DEFINITION,
    BASE_LOAD_SEVERITY_STATUS_DEFINITION,
    HEATING_ENERGY_WEEKEND_REDUCTION_STATUS_DEFINITION,
    PV_DTC_STATUS_DEFINITION,
    PV_PERFORMANCE_REDUCTION_STATUS_DEFINITION,
    WATER_LOSS_DTC_STATUS_DEFINITION,
} from '@/config/kpi/statusConfig';

/* Import types */
import { calculateSystemDashboardKpiStatus } from '@/lib/overview/systemDashboardKpiStatus';

/**
 * IMPORTANT PLACE: Base load kpi calculations
 *
 * Status: -1 -> Implausible
 * Status: 0 -> OK
 * Status: 1 -> Warning
 * Status: 2 -> Critical
 */
export function getBaseLoadStatus(
    costDay: number | null | undefined,
): number {
    return calculateSystemDashboardKpiStatus(
        BASE_LOAD_COST_DAY_STATUS_DEFINITION,
        { CostDay: costDay },
    );
}

/**
 * IMPORTANT PLACE: Normalizes the base-load severity sensor value for the shared status UI.
 *
 * Status: -1 -> Implausible
 * Status: 0 -> OK
 * Status: 1 -> Warning
 * Status: 2 -> Critical
 */
export function getBaseLoadSeverityStatus(
    severity: number | null | undefined,
): number {
    return calculateSystemDashboardKpiStatus(
        BASE_LOAD_SEVERITY_STATUS_DEFINITION,
        { Severity: severity },
    );
}

/**
 * IMPORTANT PLACE: PV kpi calculations
 *
 * Status: -1 -> Implausible
 * Status: 0 -> OK
 * Status: 1 -> Warning
 * Status: 2 -> Critical
 */
export function getPvPredictionStatus(
    dtc: number | null | undefined,
): number {
    return calculateSystemDashboardKpiStatus(PV_DTC_STATUS_DEFINITION, { dtc });
}

/**
 * IMPORTANT PLACE: Calculates the PV performance reduction status from the actual-to-expected ratio.
 * IMPORTANT PLACE: Ratios at or above the warning threshold do not represent a relevant reduction.
 *
 * Status: -1 -> Implausible
 * Status: 0 -> OK
 * Status: 1 -> Warning
 * Status: 2 -> Critical
 */
export function getPvPredictionPerformanceReductionStatus(
    ratio: number | null | undefined,
): number {
    return calculateSystemDashboardKpiStatus(
        PV_PERFORMANCE_REDUCTION_STATUS_DEFINITION,
        { ratio_30: ratio },
    );
}

/**
 * IMPORTANT PLACE: Heating energy kpi calculations
 *
 * Status: -1 -> Implausible
 * Status: 0 -> OK
 * Status: 1 -> Warning
 * Status: 2 -> Critical
 */
export function getHeatingEnergyStatus(
    weekendReductionPercent: number | null | undefined,
): number {
    return calculateSystemDashboardKpiStatus(
        HEATING_ENERGY_WEEKEND_REDUCTION_STATUS_DEFINITION,
        { WeekendReductionPercent: weekendReductionPercent },
    );
}

/**
 * IMPORTANT PLACE: Water-loss kpi calculations
 *
 * Status: 0 -> OK
 * Status: 1 -> Warning
 * Status: 2 -> Critical
 */
export function getWaterLossStatus(
    dtcLast7Days: number | null | undefined,
): number {
    return calculateSystemDashboardKpiStatus(
        WATER_LOSS_DTC_STATUS_DEFINITION,
        { dtc_7d: dtcLast7Days },
    );
}
